import { useState } from 'react';
import { Network, Globe, Shield, Database, Zap, Monitor, Wifi, Server, ArrowRight, Mail, Package, Truck, Layers, Info, Hash, Building, Phone } from 'lucide-react';
import { HeaderAd, ContentAd, FooterAd } from './GoogleAd';

// Type definitions
interface FieldStructure {
  field: string;
  value: string;
  description: string;
  analogy: string;
}

interface LayerData {
  name: string;
  color: string;
  icon: React.ReactElement;
  analogy: string;
  description: string;
  structure: FieldStructure[];
}

interface PortExample {
  port: number | string;
  protocol: string;
  service: string;
  analogy: string;
}

interface PortCategory {
  name: string;
  color: string;
  icon: React.ReactElement;
  analogy: string;
  description: string;
  examples: PortExample[];
}

interface OSILayer {
  id: number;
  name: string;
  icon: React.ReactElement;
  protocols: string[];
  description: string;
  examples: string;
  color: string;
}

const HTTPPacketJourney = ({ isDarkTheme = false }: { isDarkTheme?: boolean }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const journeySteps = [
    {
      step: 0,
      title: "📝 Application Layer",
      action: "Writing the Letter",
      technical: "HTTP Request Created",
      description: "You type 'google.com' in your browser. Your browser creates an HTTP GET request asking for Google's homepage.",
      analogy: "Like writing a letter saying 'Please send me your newspaper' and addressing it to 'Google Newspaper Company'",
      data: "GET / HTTP/1.1\nHost: google.com",
      icon: <Monitor className="w-6 h-6" />
    },
    {
      step: 1,
      title: "🛡️ Transport Layer",
      action: "Addressing the Envelope",
      technical: "TCP Header Added",
      description: "TCP adds port numbers (source: random port, destination: port 80 for HTTP) and sequence numbers for reliable delivery.",
      analogy: "Like putting your letter in an envelope with your return address and the recipient's address, plus a tracking number",
      data: "TCP Header: Src:12345 → Dst:80\nSequence: 1000, ACK: 0\n+ HTTP Request",
      icon: <Shield className="w-6 h-6" />
    },
    {
      step: 2,
      title: "🌐 Network Layer",
      action: "Postal Service Routing",
      technical: "IP Header Added",
      description: "IP adds source and destination IP addresses and handles routing decisions across networks.",
      analogy: "Like the postal service adding routing stamps and zip codes to determine which trucks and routes to use",
      data: "IP Header: 192.168.1.100 → 172.217.164.110\nTTL: 64, Protocol: TCP\n+ TCP + HTTP",
      icon: <Globe className="w-6 h-6" />
    },
    {
      step: 3,
      title: "📡 Data Link Layer",
      action: "Local Mail Carrier",
      technical: "Ethernet Header Added",
      description: "Ethernet adds MAC addresses for the next hop (your router) and error detection codes.",
      analogy: "Like your local mail carrier putting the letter in their mail truck with a local delivery tag",
      data: "Ethernet: AA:BB:CC → DD:EE:FF\nType: IPv4, CRC: ✓\n+ IP + TCP + HTTP",
      icon: <Wifi className="w-6 h-6" />
    },
    {
      step: 4,
      title: "⚡ Physical Layer",
      action: "Physical Transportation",
      technical: "Electrical Signals Transmitted",
      description: "The data becomes electrical signals traveling through cables, or radio waves through WiFi.",
      analogy: "Like the physical trucks, planes, and roads that carry the mail across the country",
      data: "010110100101... (Binary data as electrical signals)",
      icon: <Server className="w-6 h-6" />
    },
    {
      step: 5,
      title: "🎯 Arrival & Unpacking",
      action: "Mail Delivery & Opening",
      technical: "Server Receives & Processes",
      description: "Google's server receives the packet, removes each layer's header, and processes your HTTP request.",
      analogy: "Like Google receiving your letter, opening all the envelopes, reading your request, and preparing a response",
      data: "HTTP Response: 200 OK\nContent: <html>Google Homepage</html>",
      icon: <Package className="w-6 h-6" />
    }
  ];

  const startAnimation = () => {
    setIsAnimating(true);
    setCurrentStep(0);
    
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= journeySteps.length - 1) {
          clearInterval(interval);
          setIsAnimating(false);
          return prev;
        }
        return prev + 1;
      });
    }, 2000);
  };

  return (
    <div>
      <div className="flex justify-center mb-8">
        <button
          onClick={startAnimation}
          disabled={isAnimating}
          className={`px-6 py-3 rounded-lg font-semibold text-white transition-all ${
            isAnimating 
              ? isDarkTheme 
                ? 'bg-gray-600 cursor-not-allowed' 
                : 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
          }`}
        >
          {isAnimating ? 'Animation Running...' : '🚀 Start Packet Journey'}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Journey Visualization */}
        <div className="space-y-4">
          <h3 className={`text-xl font-semibold mb-4 ${
            isDarkTheme ? 'text-gray-200' : 'text-gray-800'
          }`}>Packet Journey Steps</h3>
          {journeySteps.map((step, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 transition-all duration-500 ${
                index <= currentStep
                  ? isDarkTheme
                    ? 'border-blue-400 bg-blue-900 shadow-md'
                    : 'border-blue-500 bg-blue-50 shadow-md'
                  : isDarkTheme
                    ? 'border-gray-600 bg-gray-700'
                    : 'border-gray-200 bg-gray-50'
              } ${index === currentStep ? 'ring-2 ring-blue-300 scale-105' : ''}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  index <= currentStep 
                    ? 'bg-blue-500 text-white' 
                    : isDarkTheme
                      ? 'bg-gray-600 text-gray-300'
                      : 'bg-gray-300 text-gray-600'
                }`}>
                  {step.icon}
                </div>
                <div>
                  <h4 className={`font-bold ${
                    isDarkTheme ? 'text-gray-200' : 'text-gray-800'
                  }`}>{step.title}</h4>
                  <p className={`text-sm ${
                    isDarkTheme ? 'text-gray-400' : 'text-gray-600'
                  }`}>{step.action}</p>
                </div>
                {index < journeySteps.length - 1 && (
                  <ArrowRight className={`w-5 h-5 ml-auto ${
                    index < currentStep 
                      ? 'text-blue-500' 
                      : isDarkTheme 
                        ? 'text-gray-600' 
                        : 'text-gray-300'
                  }`} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Current Step Details */}
        <div className={`rounded-lg p-6 ${
          isDarkTheme 
            ? 'bg-gradient-to-br from-gray-700 to-gray-800 border border-gray-600' 
            : 'bg-gradient-to-br from-gray-50 to-gray-100'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 ${
            isDarkTheme ? 'text-gray-200' : 'text-gray-800'
          }`}>Current Step Details</h3>
          {currentStep < journeySteps.length ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-500 text-white rounded-lg">
                  {journeySteps[currentStep].icon}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800">
                    {journeySteps[currentStep].title}
                  </h4>
                  <p className="text-blue-600 font-medium">
                    {journeySteps[currentStep].technical}
                  </p>
                </div>
              </div>

              <div className={`rounded-lg border ${
                isDarkTheme ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
              }`}>
                <h5 className={`font-semibold mb-2 ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>📖 What Happens:</h5>
                <p className={`mb-3 ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>{journeySteps[currentStep].description}</p>
                
                <h5 className={`font-semibold mb-2 ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>🏤 Postal Analogy:</h5>
                <p className={`mb-3 italic ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>{journeySteps[currentStep].analogy}</p>

                <h5 className={`font-semibold mb-2 ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>💻 Data Structure:</h5>
                <pre className={`p-3 rounded text-sm font-mono overflow-x-auto ${
                  isDarkTheme 
                    ? 'bg-gray-900 text-gray-300 border border-gray-700' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {journeySteps[currentStep].data}
                </pre>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Mail className={`w-16 h-16 mx-auto mb-4 ${
                isDarkTheme ? 'text-gray-600' : 'text-gray-400'
              }`} />
              <p className={isDarkTheme ? 'text-gray-400' : 'text-gray-600'}>Click "Start Packet Journey" to see how HTTP requests travel through network layers!</p>
            </div>
          )}
        </div>
      </div>

      {/* Key Insights */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className={`p-4 rounded-lg ${
          isDarkTheme 
            ? 'bg-gradient-to-br from-green-800 to-green-900 border border-green-700' 
            : 'bg-gradient-to-br from-green-50 to-green-100'
        }`}>
          <h4 className={`font-semibold mb-2 flex items-center gap-2 ${
            isDarkTheme ? 'text-green-200' : 'text-green-800'
          }`}>
            <Package className="w-5 h-5" />
            Encapsulation Process
          </h4>
          <p className={`text-sm ${
            isDarkTheme ? 'text-green-300' : 'text-green-700'
          }`}>
            Each layer wraps the data from above with its own header, like putting letters in multiple envelopes 
            with different addressing and routing information.
          </p>
        </div>
        <div className={`p-4 rounded-lg ${
          isDarkTheme 
            ? 'bg-gradient-to-br from-orange-800 to-orange-900 border border-orange-700' 
            : 'bg-gradient-to-br from-orange-50 to-orange-100'
        }`}>
          <h4 className={`font-semibold mb-2 flex items-center gap-2 ${
            isDarkTheme ? 'text-orange-200' : 'text-orange-800'
          }`}>
            <Truck className="w-5 h-5" />
            Return Journey
          </h4>
          <p className={`text-sm ${
            isDarkTheme ? 'text-orange-300' : 'text-orange-700'
          }`}>
            Google's response follows the same process in reverse: Application → Transport → Network → Data Link → Physical, 
            like sending a reply letter back through the postal system.
          </p>
        </div>
      </div>
    </div>
  );
};

const PacketStructureExplorer = ({ isDarkTheme = false }: { isDarkTheme?: boolean }) => {
  const [selectedLayer, setSelectedLayer] = useState<string>('application');
  
  const packetLayers: Record<string, LayerData> = {
    application: {
      name: "Application Layer",
      color: "bg-purple-500",
      icon: <Monitor className="w-5 h-5" />,
      analogy: "📝 The Actual Letter Content",
      description: "This is your actual message - the HTTP request asking for a webpage",
      structure: [
        { field: "HTTP Method", value: "GET", description: "What you want to do", analogy: "Like saying 'Please give me' at the start of your letter" },
        { field: "URL Path", value: "/index.html", description: "What you're requesting", analogy: "The specific item you're asking for" },
        { field: "HTTP Version", value: "HTTP/1.1", description: "Communication standard", analogy: "Like writing in English vs French - both sides need to understand" },
        { field: "Host Header", value: "google.com", description: "Which server to contact", analogy: "The name of the company you're writing to" },
        { field: "User-Agent", value: "Chrome/91.0", description: "What browser you're using", analogy: "Your signature identifying who you are" }
      ]
    },
    transport: {
      name: "Transport Layer (TCP)",
      color: "bg-green-500",
      icon: <Shield className="w-5 h-5" />,
      analogy: "📮 The Envelope with Delivery Instructions",
      description: "TCP adds reliability, port numbers, and flow control information",
      structure: [
        { field: "Source Port", value: "49152", description: "Your application's port", analogy: "Your apartment number - so replies come back to the right place" },
        { field: "Destination Port", value: "80", description: "Web server's port", analogy: "The department (like 'Customer Service') at the company" },
        { field: "Sequence Number", value: "1000", description: "Packet order number", analogy: "Page number if your letter has multiple pages" },
        { field: "Acknowledgment", value: "0", description: "Last received packet", analogy: "Receipt confirmation - 'I got your page 5'" },
        { field: "Window Size", value: "65535", description: "Flow control", analogy: "How many letters you can handle at once" },
        { field: "Checksum", value: "0x7A3B", description: "Error detection", analogy: "A security seal to detect if envelope was tampered with" }
      ]
    },
    network: {
      name: "Network Layer (IP)",
      color: "bg-yellow-500",
      icon: <Globe className="w-5 h-5" />,
      analogy: "🏷️ The Shipping Label with Addresses",
      description: "IP handles addressing and routing across different networks",
      structure: [
        { field: "Version", value: "4", description: "IP version (IPv4)", analogy: "The postal system version (like ZIP codes vs old addressing)" },
        { field: "Header Length", value: "20 bytes", description: "Size of IP header", analogy: "How much space the shipping label takes" },
        { field: "Type of Service", value: "0x00", description: "Priority level", analogy: "Express mail vs regular mail priority" },
        { field: "Total Length", value: "576 bytes", description: "Entire packet size", analogy: "Total weight of your package" },
        { field: "Source IP", value: "192.168.1.100", description: "Your IP address", analogy: "Your complete return address with street, city, state" },
        { field: "Destination IP", value: "172.217.164.110", description: "Google's IP address", analogy: "The complete destination address" },
        { field: "Time To Live", value: "64", description: "Max hops before discard", analogy: "Maximum number of post offices that can handle this before giving up" }
      ]
    },
    datalink: {
      name: "Data Link Layer (Ethernet)",
      color: "bg-orange-500",
      icon: <Wifi className="w-5 h-5" />,
      analogy: "🚚 The Local Delivery Truck Label",
      description: "Ethernet handles local network delivery and error detection",
      structure: [
        { field: "Destination MAC", value: "AA:BB:CC:DD:EE:FF", description: "Next hop MAC address", analogy: "The delivery truck's route tag for the next stop" },
        { field: "Source MAC", value: "11:22:33:44:55:66", description: "Your network card's MAC", analogy: "Your local mail carrier's ID number" },
        { field: "EtherType", value: "0x0800", description: "Protocol type (IPv4)", analogy: "Type of cargo label (letters vs packages vs newspapers)" },
        { field: "Frame Check", value: "0x12345678", description: "Error detection code", analogy: "Quality control sticker to verify delivery integrity" }
      ]
    },
    physical: {
      name: "Physical Layer",
      color: "bg-red-500",
      icon: <Server className="w-5 h-5" />,
      analogy: "⚡ The Actual Transportation Medium",
      description: "The physical transmission of bits as electrical signals, light, or radio waves",
      structure: [
        { field: "Signal Type", value: "Electrical", description: "Type of physical signal", analogy: "The actual trucks, planes, trains carrying your mail" },
        { field: "Bit Rate", value: "1 Gbps", description: "Speed of transmission", analogy: "How fast the transportation moves (express vs ground shipping)" },
        { field: "Encoding", value: "Manchester", description: "How bits are represented", analogy: "The specific way packages are loaded and secured in vehicles" },
        { field: "Medium", value: "Copper Cable", description: "Physical connection", analogy: "The actual roads, railways, or flight paths used" }
      ]
    }
  };

  const getPacketVisualization = () => {
    const layers = ['physical', 'datalink', 'network', 'transport', 'application'];
    return layers.map((layerKey) => {
      const layer = packetLayers[layerKey];
      const isSelected = selectedLayer === layerKey;
      const showDetails = layers.slice(layers.indexOf(selectedLayer)).includes(layerKey);
      
      return (
        <div
          key={layerKey}
          className={`border-2 transition-all duration-300 ${
            isSelected ? 'border-blue-500 shadow-lg' : 'border-gray-200'
          } ${showDetails ? 'opacity-100' : 'opacity-60'}`}
        >
          <div
            onClick={() => setSelectedLayer(layerKey)}
            className={`${layer.color} text-white p-3 cursor-pointer hover:brightness-110 transition-all`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {layer.icon}
                <span className="font-semibold">{layer.name}</span>
              </div>
              <div className="text-xs opacity-90">{layer.analogy}</div>
            </div>
          </div>
          {showDetails && (
            <div className="bg-white p-3 border-t">
              <div className="text-xs text-gray-600 mb-2">{layer.description}</div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {layer.structure.slice(0, 4).map((field: FieldStructure, idx: number) => (
                  <div key={idx} className="flex justify-between">
                    <span className="font-medium text-gray-700">{field.field}:</span>
                    <span className="text-gray-600">{field.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Packet Structure Visualization */}
        <div>
          <h3 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${
            isDarkTheme ? 'text-gray-200' : 'text-gray-800'
          }`}>
            <Layers className="w-6 h-6" />
            Packet Structure (Click layers to explore)
          </h3>
          <div className={`border rounded-lg overflow-hidden ${
            isDarkTheme ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <div className={`p-3 text-center text-sm font-medium ${
              isDarkTheme ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
            }`}>
              📦 Complete Network Packet
            </div>
            {getPacketVisualization()}
          </div>
          <div className={`mt-4 text-sm p-3 rounded-lg ${
            isDarkTheme 
              ? 'text-gray-300 bg-blue-900 border border-blue-700' 
              : 'text-gray-600 bg-blue-50'
          }`}>
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Reading Direction:</strong> Data flows from top (Application) to bottom (Physical) when sending, 
            and bottom to top when receiving. Each layer adds its own "wrapper" around the data from above.
          </div>
        </div>

        {/* Selected Layer Details */}
        <div>
          <h3 className={`text-xl font-semibold mb-4 ${
            isDarkTheme ? 'text-gray-200' : 'text-gray-800'
          }`}>Layer Details</h3>
          <div className={`rounded-lg border shadow-sm ${
            isDarkTheme ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}>
            <div className={`${packetLayers[selectedLayer].color} text-white p-4 rounded-t-lg`}>
              <div className="flex items-center gap-3">
                {packetLayers[selectedLayer].icon}
                <div>
                  <h4 className="text-lg font-bold">{packetLayers[selectedLayer].name}</h4>
                  <p className="text-sm opacity-90">{packetLayers[selectedLayer].analogy}</p>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <p className={`mb-4 ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-700'
              }`}>{packetLayers[selectedLayer].description}</p>
              
              <h5 className={`font-semibold mb-3 ${
                isDarkTheme ? 'text-gray-200' : 'text-gray-800'
              }`}>Header Fields & Structure:</h5>
              <div className="space-y-3">
                {packetLayers[selectedLayer].structure.map((field: FieldStructure, index: number) => (
                  <div key={index} className={`p-3 rounded-lg ${
                    isDarkTheme ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`font-medium ${
                        isDarkTheme ? 'text-gray-200' : 'text-gray-800'
                      }`}>{field.field}</span>
                      <span className={`text-sm px-2 py-1 rounded ${
                        isDarkTheme 
                          ? 'bg-blue-900 text-blue-200 border border-blue-700' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>{field.value}</span>
                    </div>
                    <p className={`text-sm mb-1 ${
                      isDarkTheme ? 'text-gray-400' : 'text-gray-600'
                    }`}>{field.description}</p>
                    <p className={`text-xs italic ${
                      isDarkTheme ? 'text-blue-300' : 'text-blue-600'
                    }`}>💡 {field.analogy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Encapsulation Process */}
      <div className="mt-8 bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-indigo-800 mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" />
          The Encapsulation Process: Building the Packet
        </h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white p-4 rounded-lg">
            <h5 className="font-semibold text-purple-700 mb-2">1. Start with Data</h5>
            <p className="text-gray-600">
              Your HTTP request is like writing a letter with your specific request.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h5 className="font-semibold text-purple-700 mb-2">2. Add Headers</h5>
            <p className="text-gray-600">
              Each layer adds its "envelope" with addressing and delivery instructions.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h5 className="font-semibold text-purple-700 mb-2">3. Transmit</h5>
            <p className="text-gray-600">
              The complete package travels through the physical network infrastructure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PortNumbersExplorer = ({ isDarkTheme }: { isDarkTheme: boolean }) => {
  const [selectedPortCategory, setSelectedPortCategory] = useState<string>('wellknown');
  
  const portCategories: Record<string, PortCategory> = {
    wellknown: {
      name: "Well-Known Ports (0-1023)",
      color: "bg-blue-500",
      icon: <Building className="w-5 h-5" />,
      analogy: "🏢 Famous Building Addresses",
      description: "Reserved for system services and common protocols. These are like famous landmarks - everyone knows where they are.",
      examples: [
        { port: 20, protocol: "FTP", service: "File Transfer (Data)", analogy: "The main entrance to the File Storage Building" },
        { port: 21, protocol: "FTP", service: "File Transfer (Control)", analogy: "The information desk at the File Storage Building" },
        { port: 22, protocol: "SSH", service: "Secure Shell", analogy: "The secure entrance with key card access" },
        { port: 23, protocol: "Telnet", service: "Remote Login", analogy: "The old, unsecured back entrance (rarely used now)" },
        { port: 25, protocol: "SMTP", service: "Email Sending", analogy: "The Post Office mailbox for outgoing mail" },
        { port: 53, protocol: "DNS", service: "Domain Name System", analogy: "The Phone Book/Directory Service office" },
        { port: 80, protocol: "HTTP", service: "Web Pages", analogy: "The main entrance to the Web Store" },
        { port: 110, protocol: "POP3", service: "Email Retrieval", analogy: "Your personal mailbox pickup window" },
        { port: 143, protocol: "IMAP", service: "Email Access", analogy: "The email reading room where you can organize mail" },
        { port: 443, protocol: "HTTPS", service: "Secure Web", analogy: "The secure, encrypted entrance to the Web Store" },
        { port: 993, protocol: "IMAPS", service: "Secure Email", analogy: "The secure, private email reading room" },
        { port: 995, protocol: "POP3S", service: "Secure Email Retrieval", analogy: "Your secure, private mailbox with encryption" }
      ]
    },
    registered: {
      name: "Registered Ports (1024-49151)",
      color: "bg-green-500",
      icon: <Hash className="w-5 h-5" />,
      analogy: "🏬 Registered Business Addresses",
      description: "Assigned to specific applications by IANA. Like registered business addresses - officially assigned but not as universally known.",
      examples: [
        { port: 1433, protocol: "TCP", service: "Microsoft SQL Server", analogy: "The Microsoft Database Office suite" },
        { port: 1521, protocol: "TCP", service: "Oracle Database", analogy: "The Oracle Database headquarters" },
        { port: 3306, protocol: "TCP", service: "MySQL Database", analogy: "The MySQL Database branch office" },
        { port: 3389, protocol: "TCP", service: "Remote Desktop", analogy: "The Remote Control Center for Windows computers" },
        { port: 5432, protocol: "TCP", service: "PostgreSQL", analogy: "The PostgreSQL Database regional office" },
        { port: 8080, protocol: "HTTP", service: "Alternative Web", analogy: "The alternate entrance to the Web Store (when main entrance is busy)" },
        { port: 8443, protocol: "HTTPS", service: "Alternative Secure Web", analogy: "The alternate secure entrance to the Web Store" }
      ]
    },
    dynamic: {
      name: "Dynamic Ports (49152-65535)",
      color: "bg-purple-500",
      icon: <Phone className="w-5 h-5" />,
      analogy: "📞 Temporary Phone Extensions",
      description: "Used for temporary connections by client applications. Like temporary phone extensions assigned when you call a company.",
      examples: [
        { port: "49152-65535", protocol: "TCP/UDP", service: "Client Applications", analogy: "Temporary extension numbers assigned when you call a business" },
        { port: "Random", protocol: "TCP", service: "Web Browser", analogy: "Your temporary extension when browsing websites" },
        { port: "Random", protocol: "UDP", service: "Video Call", analogy: "Your temporary line for video conferences" },
        { port: "Random", protocol: "TCP", service: "Email Client", analogy: "Your temporary connection when checking email" }
      ]
    }
  };

  const uniquenessRules = [
    {
      title: "🏠 Same IP Address",
      description: "Port numbers must be unique per IP address and protocol combination",
      analogy: "Like apartment numbers in a building - each apartment has a unique number, but different buildings can have the same apartment numbers",
      example: "192.168.1.100:80 and 192.168.1.101:80 can both exist (different buildings)"
    },
    {
      title: "🔄 Protocol Separation", 
      description: "TCP and UDP port numbers are separate namespaces",
      analogy: "Like having separate elevator banks for residents vs visitors - same floor numbers but different elevators",
      example: "TCP port 80 and UDP port 80 can both be used on the same computer"
    },
    {
      title: "⏰ Time-based Sharing",
      description: "The same port can be reused after a connection ends",
      analogy: "Like a restaurant table - once customers leave, the same table number can be assigned to new customers",
      example: "After you close your web browser, port 49152 becomes available for other applications"
    },
    {
      title: "👥 Multiple Services",
      description: "One application can listen on multiple ports simultaneously",
      analogy: "Like a company having multiple phone numbers for different departments",
      example: "A web server can listen on both port 80 (HTTP) and port 443 (HTTPS)"
    }
  ];

  return (
    <div>
      {/* Port Categories Explorer */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Port Categories */}
        <div>
          <h3 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${
            isDarkTheme ? 'text-white' : 'text-gray-800'
          }`}>
            <Hash className="w-6 h-6" />
            Port Number Categories
          </h3>
          <div className="space-y-3">
            {Object.entries(portCategories).map(([key, category]) => (
              <div
                key={key}
                onClick={() => setSelectedPortCategory(key)}
                className={`p-4 rounded-lg cursor-pointer transition-all duration-300 border-2 ${
                  selectedPortCategory === key
                    ? 'border-blue-500 shadow-lg'
                    : isDarkTheme
                    ? 'border-gray-600 hover:border-gray-500'
                    : 'border-gray-200 hover:border-gray-300'
                } ${category.color} text-white`}
              >
                <div className="flex items-center gap-3">
                  {category.icon}
                  <div>
                    <h4 className="font-bold text-lg">{category.name}</h4>
                    <p className="text-sm opacity-90">{category.analogy}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Layer Information */}
          <div className={`mt-6 p-4 rounded-lg ${
            isDarkTheme 
              ? 'bg-gradient-to-br from-green-900 to-blue-900 border border-green-700' 
              : 'bg-gradient-to-br from-green-50 to-blue-50'
          }`}>
            <h4 className={`font-semibold mb-2 flex items-center gap-2 ${
              isDarkTheme ? 'text-green-200' : 'text-green-800'
            }`}>
              <Shield className="w-5 h-5" />
              Transport Layer (Layer 4)
            </h4>
            <p className={`text-sm ${
              isDarkTheme ? 'text-green-300' : 'text-green-700'
            }`}>
              Port numbers operate at the <strong>Transport Layer</strong> and work with <strong>TCP</strong> and <strong>UDP</strong> protocols to identify specific applications or services on a device.
            </p>
          </div>
        </div>

        {/* Selected Category Details */}
        <div>
          <h3 className={`text-xl font-semibold mb-4 ${
            isDarkTheme ? 'text-white' : 'text-gray-800'
          }`}>Port Details</h3>
          <div className={`rounded-lg border shadow-sm ${
            isDarkTheme ? 'bg-gray-800 border-gray-700' : 'bg-white'
          }`}>
            <div className={`${portCategories[selectedPortCategory].color} text-white p-4 rounded-t-lg`}>
              <div className="flex items-center gap-3">
                {portCategories[selectedPortCategory].icon}
                <div>
                  <h4 className="text-lg font-bold">{portCategories[selectedPortCategory].name}</h4>
                  <p className="text-sm opacity-90">{portCategories[selectedPortCategory].analogy}</p>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <p className={`mb-4 ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-700'
              }`}>{portCategories[selectedPortCategory].description}</p>
              
              <h5 className={`font-semibold mb-3 ${
                isDarkTheme ? 'text-white' : 'text-gray-800'
              }`}>Common Examples:</h5>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {portCategories[selectedPortCategory].examples.map((example: PortExample, index: number) => (
                  <div key={index} className={`p-3 rounded-lg ${
                    isDarkTheme ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-blue-500">Port {example.port}</span>
                        <span className={`text-sm px-2 py-1 rounded ${
                          isDarkTheme 
                            ? 'bg-gray-600 text-gray-300' 
                            : 'bg-gray-200 text-gray-700'
                        }`}>{example.protocol}</span>
                      </div>
                    </div>
                    <p className={`text-sm font-medium mb-1 ${
                      isDarkTheme ? 'text-gray-200' : 'text-gray-800'
                    }`}>{example.service}</p>
                    <p className="text-xs text-blue-500 italic">💡 {example.analogy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Port Uniqueness Rules */}
      <div className={`p-6 rounded-lg mb-8 ${
        isDarkTheme 
          ? 'bg-gradient-to-br from-yellow-900 to-orange-900 border border-yellow-700' 
          : 'bg-gradient-to-br from-yellow-50 to-orange-50'
      }`}>
        <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
          isDarkTheme ? 'text-yellow-200' : 'text-orange-800'
        }`}>
          <Building className="w-5 h-5" />
          How Port Numbers Stay Unique: The Building Analogy
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          {uniquenessRules.map((rule, index) => (
            <div key={index} className={`p-4 rounded-lg ${
              isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
            }`}>
              <h5 className={`font-semibold mb-2 ${
                isDarkTheme ? 'text-yellow-300' : 'text-orange-700'
              }`}>{rule.title}</h5>
              <p className={`text-sm mb-2 ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-700'
              }`}>{rule.description}</p>
              <p className="text-xs text-blue-500 italic mb-2">💡 {rule.analogy}</p>
              <p className={`text-xs p-2 rounded ${
                isDarkTheme 
                  ? 'text-gray-400 bg-gray-700' 
                  : 'text-gray-600 bg-gray-100'
              }`}>
                <strong>Example:</strong> {rule.example}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Protocol Relationship */}
      <div className={`p-6 rounded-lg ${
        isDarkTheme 
          ? 'bg-gradient-to-br from-indigo-900 to-purple-900 border border-indigo-700' 
          : 'bg-gradient-to-br from-indigo-50 to-purple-50'
      }`}>
        <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
          isDarkTheme ? 'text-indigo-200' : 'text-indigo-800'
        }`}>
          <Network className="w-5 h-5" />
          Port Numbers & Protocol Relationship
        </h4>
        <div className="grid md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg ${
            isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <h5 className={`font-semibold mb-2 flex items-center gap-2 ${
              isDarkTheme ? 'text-blue-400' : 'text-blue-700'
            }`}>
              <Shield className="w-4 h-4" />
              TCP Ports
            </h5>
            <p className={`text-sm mb-2 ${
              isDarkTheme ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Used for reliable, connection-oriented communication
            </p>
            <p className="text-xs text-blue-500 italic">
              Like registered mail with delivery confirmation - slower but guaranteed delivery
            </p>
            <div className={`text-xs mt-2 ${
              isDarkTheme ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <strong>Examples:</strong> HTTP (80), HTTPS (443), SSH (22)
            </div>
          </div>
          <div className={`p-4 rounded-lg ${
            isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <h5 className={`font-semibold mb-2 flex items-center gap-2 ${
              isDarkTheme ? 'text-green-400' : 'text-green-700'
            }`}>
              <Zap className="w-4 h-4" />
              UDP Ports
            </h5>
            <p className={`text-sm mb-2 ${
              isDarkTheme ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Used for fast, connectionless communication
            </p>
            <p className="text-xs text-blue-500 italic">
              Like postcards - fast delivery but no guarantee of arrival
            </p>
            <div className={`text-xs mt-2 ${
              isDarkTheme ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <strong>Examples:</strong> DNS (53), DHCP (67/68), Video streaming
            </div>
          </div>
          <div className={`p-4 rounded-lg ${
            isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <h5 className={`font-semibold mb-2 flex items-center gap-2 ${
              isDarkTheme ? 'text-purple-400' : 'text-purple-700'
            }`}>
              <Package className="w-4 h-4" />
              Port + IP = Socket
            </h5>
            <p className={`text-sm mb-2 ${
              isDarkTheme ? 'text-gray-300' : 'text-gray-600'
            }`}>
              IP address + Port number = Complete network address
            </p>
            <p className="text-xs text-blue-500 italic">
              Like a complete mailing address: Street Address + Apartment Number
            </p>
            <div className={`text-xs mt-2 ${
              isDarkTheme ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <strong>Example:</strong> 192.168.1.100:8080
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NetworkLayersExplorer = () => {
  const [selectedLayer, setSelectedLayer] = useState<OSILayer | null>(null);
  const [activeModel, setActiveModel] = useState('osi');
  const [isDarkTheme, setIsDarkTheme] = useState(true); // Default to dark theme

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const osiLayers = [
    {
      id: 7,
      name: "Application Layer",
      icon: <Monitor className="w-6 h-6" />,
      protocols: ["HTTP/HTTPS", "FTP", "SMTP", "DNS", "DHCP", "SSH"],
      description: "Provides network services directly to applications. This is where user applications interact with the network.",
      examples: "Web browsers, email clients, file transfer applications",
      color: "bg-purple-500"
    },
    {
      id: 6,
      name: "Presentation Layer",
      icon: <Zap className="w-6 h-6" />,
      protocols: ["SSL/TLS", "JPEG", "MPEG", "ASCII", "EBCDIC"],
      description: "Handles data encryption, compression, and translation between different data formats.",
      examples: "Data encryption, image compression, character encoding",
      color: "bg-indigo-500"
    },
    {
      id: 5,
      name: "Session Layer",
      icon: <Database className="w-6 h-6" />,
      protocols: ["NetBIOS", "RPC", "SQL", "NFS"],
      description: "Manages sessions between applications, including establishing, maintaining, and terminating connections.",
      examples: "Login sessions, database connections, remote procedure calls",
      color: "bg-blue-500"
    },
    {
      id: 4,
      name: "Transport Layer",
      icon: <Shield className="w-6 h-6" />,
      protocols: ["TCP", "UDP", "SCTP"],
      description: "Provides reliable data transfer, error detection, and flow control between end systems.",
      examples: "Port numbers, reliable delivery (TCP), fast delivery (UDP)",
      color: "bg-green-500"
    },
    {
      id: 3,
      name: "Network Layer",
      icon: <Globe className="w-6 h-6" />,
      protocols: ["IP", "ICMP", "OSPF", "BGP", "RIP"],
      description: "Handles routing of data packets between different networks using logical addressing.",
      examples: "IP addresses, routing tables, packet forwarding",
      color: "bg-yellow-500"
    },
    {
      id: 2,
      name: "Data Link Layer",
      icon: <Wifi className="w-6 h-6" />,
      protocols: ["Ethernet", "Wi-Fi", "PPP", "Frame Relay"],
      description: "Provides node-to-node data transfer and handles error detection and correction within a network segment.",
      examples: "MAC addresses, Ethernet frames, wireless protocols",
      color: "bg-orange-500"
    },
    {
      id: 1,
      name: "Physical Layer",
      icon: <Server className="w-6 h-6" />,
      protocols: ["10BASE-T", "100BASE-TX", "1000BASE-T", "Fiber Optic"],
      description: "Transmits raw bit streams over physical medium like cables, radio waves, or optical fibers.",
      examples: "Cables, hubs, repeaters, electrical signals",
      color: "bg-red-500"
    }
  ];

  const tcpipLayers = [
    {
      id: 4,
      name: "Application Layer",
      icon: <Monitor className="w-6 h-6" />,
      protocols: ["HTTP/HTTPS", "FTP", "SMTP", "DNS", "DHCP", "SSH", "Telnet"],
      description: "Combines OSI layers 5-7. Provides network services directly to applications.",
      examples: "Web browsing, email, file transfer, remote access",
      color: "bg-purple-500"
    },
    {
      id: 3,
      name: "Transport Layer",
      icon: <Shield className="w-6 h-6" />,
      protocols: ["TCP", "UDP"],
      description: "Provides end-to-end communication and data integrity between applications.",
      examples: "Reliable delivery (TCP), fast delivery (UDP), port numbers",
      color: "bg-green-500"
    },
    {
      id: 2,
      name: "Internet Layer",
      icon: <Globe className="w-6 h-6" />,
      protocols: ["IP", "ICMP", "IGMP", "ARP"],
      description: "Handles routing and logical addressing across multiple networks.",
      examples: "IP addressing, routing, packet forwarding",
      color: "bg-yellow-500"
    },
    {
      id: 1,
      name: "Network Access Layer",
      icon: <Wifi className="w-6 h-6" />,
      protocols: ["Ethernet", "Wi-Fi", "PPP", "Token Ring"],
      description: "Combines OSI layers 1-2. Handles physical transmission and local network communication.",
      examples: "MAC addresses, physical transmission, local network protocols",
      color: "bg-orange-500"
    }
  ];

  const currentLayers = activeModel === 'osi' ? osiLayers : tcpipLayers;

  return (
    <div className={`max-w-6xl mx-auto p-6 min-h-screen transition-colors duration-300 ${
      isDarkTheme ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Theme Toggle Button */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 ${
            isDarkTheme 
              ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700 border border-gray-600' 
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
          title={`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`}
        >
          {isDarkTheme ? '☀️' : '🌙'}
        </button>
      </div>

      <div className="text-center mb-8">
        <h1 className={`text-4xl font-bold mb-4 flex items-center justify-center gap-3 ${
          isDarkTheme ? 'text-white' : 'text-gray-800'
        }`}>
          <Network className="w-10 h-10 text-blue-600" />
          Network Layers & Protocols
        </h1>
        <p className={`text-lg max-w-3xl mx-auto ${
          isDarkTheme ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Explore the fundamental layers of network communication and understand how data travels across networks.
        </p>
      </div>

      {/* Model Selector */}
      <div className="flex justify-center mb-8">
        <div className={`rounded-lg p-1 shadow-md ${
          isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}>
          <button
            onClick={() => setActiveModel('osi')}
            className={`px-6 py-3 rounded-md font-semibold transition-all ${
              activeModel === 'osi'
                ? 'bg-blue-600 text-white shadow-md'
                : isDarkTheme 
                  ? 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
            }`}
          >
            OSI Model (7 Layers)
          </button>
          <button
            onClick={() => setActiveModel('tcpip')}
            className={`px-6 py-3 rounded-md font-semibold transition-all ${
              activeModel === 'tcpip'
                ? 'bg-blue-600 text-white shadow-md'
                : isDarkTheme 
                  ? 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
            }`}
          >
            TCP/IP Model (4 Layers)
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Layer Stack Visualization */}
        <div className={`rounded-xl shadow-lg p-6 ${
          isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}>
          <h2 className={`text-2xl font-bold mb-6 text-center ${
            isDarkTheme ? 'text-white' : 'text-gray-800'
          }`}>
            {activeModel === 'osi' ? 'OSI Model' : 'TCP/IP Model'} Layers
          </h2>
          <div className="space-y-2">
            {currentLayers.slice().reverse().map((layer) => (
              <div
                key={layer.id}
                onClick={() => setSelectedLayer(layer)}
                className={`p-4 rounded-lg cursor-pointer transition-all duration-300 border-2 ${
                  selectedLayer?.id === layer.id
                    ? 'border-blue-500 shadow-lg scale-105'
                    : 'border-transparent hover:border-gray-300 hover:shadow-md'
                } ${layer.color} text-white`}
              >
                <div className="flex items-center gap-3">
                  {layer.icon}
                  <div>
                    <div className="font-bold text-lg">Layer {layer.id}</div>
                    <div className="text-sm opacity-90">{layer.name}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Layer Details */}
        <div className={`rounded-xl shadow-lg p-6 ${
          isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}>
          {selectedLayer ? (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-lg ${selectedLayer.color} text-white`}>
                  {selectedLayer.icon}
                </div>
                <div>
                  <h2 className={`text-2xl font-bold ${
                    isDarkTheme ? 'text-white' : 'text-gray-800'
                  }`}>{selectedLayer.name}</h2>
                  <p className={isDarkTheme ? 'text-gray-400' : 'text-gray-600'}>Layer {selectedLayer.id}</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDarkTheme ? 'text-gray-200' : 'text-gray-800'
                  }`}>Description</h3>
                  <p className={`leading-relaxed ${
                    isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                  }`}>{selectedLayer.description}</p>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    isDarkTheme ? 'text-gray-200' : 'text-gray-800'
                  }`}>Common Protocols</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedLayer.protocols.map((protocol: string, index: number) => (
                      <span
                        key={index}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          isDarkTheme 
                            ? 'bg-blue-900 text-blue-200 border border-blue-700'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {protocol}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDarkTheme ? 'text-gray-200' : 'text-gray-800'
                  }`}>Examples</h3>
                  <p className={isDarkTheme ? 'text-gray-300' : 'text-gray-700'}>{selectedLayer.examples}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Network className={`w-16 h-16 mx-auto mb-4 ${
                isDarkTheme ? 'text-gray-600' : 'text-gray-400'
              }`} />
              <h3 className={`text-xl font-semibold mb-2 ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-600'
              }`}>Select a Layer</h3>
              <p className={isDarkTheme ? 'text-gray-400' : 'text-gray-500'}>Click on any layer in the stack to learn more about its protocols and functions.</p>
            </div>
          )}
        </div>
      </div>

      {/* Header Advertisement */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <HeaderAd isDarkTheme={isDarkTheme} />
      </div>

      {/* HTTP Packet Journey */}
      <div className={`mt-12 rounded-xl shadow-lg p-6 ${
        isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${
          isDarkTheme ? 'text-white' : 'text-gray-800'
        }`}>
          <Mail className="w-8 h-8 text-blue-600" />
          HTTP Packet Journey: The Postal Mail Analogy
        </h2>
        
        <div className={`p-6 rounded-lg mb-8 ${
          isDarkTheme 
            ? 'bg-gradient-to-br from-blue-900 to-indigo-900 border border-blue-700' 
            : 'bg-gradient-to-br from-blue-50 to-indigo-50'
        }`}>
          <h3 className={`text-lg font-semibold mb-3 ${
            isDarkTheme ? 'text-blue-200' : 'text-blue-800'
          }`}>📬 Think of Network Communication Like Sending Mail</h3>
          <p className={`leading-relaxed ${
            isDarkTheme ? 'text-blue-300' : 'text-blue-700'
          }`}>
            When you send a letter through the postal system, it goes through multiple stages and handlers. 
            Similarly, when your browser requests a webpage, the HTTP request travels through network layers, 
            with each layer adding its own "envelope" and handling instructions, just like the postal system 
            adds stamps, routing labels, and delivery instructions.
          </p>
        </div>

        <HTTPPacketJourney isDarkTheme={isDarkTheme} />
      </div>

      {/* Packet Structure Details */}
      <div className={`mt-12 rounded-xl shadow-lg p-6 ${
        isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${
          isDarkTheme ? 'text-white' : 'text-gray-800'
        }`}>
          <Layers className="w-8 h-8 text-green-600" />
          Packet Structure Deep Dive
        </h2>
        
        <div className={`p-6 rounded-lg mb-8 ${
          isDarkTheme 
            ? 'bg-gradient-to-br from-green-900 to-emerald-900 border border-green-700' 
            : 'bg-gradient-to-br from-green-50 to-emerald-50'
        }`}>
          <h3 className={`text-lg font-semibold mb-3 ${
            isDarkTheme ? 'text-green-200' : 'text-green-800'
          }`}>📋 Understanding What's Inside Each Layer</h3>
          <p className={`leading-relaxed ${
            isDarkTheme ? 'text-green-300' : 'text-green-700'
          }`}>
            Just like a letter has different parts (the message, envelope, postal stamps, delivery labels), 
            network packets have headers at each layer containing specific information needed for delivery. 
            Let's explore what each layer adds to the packet and why it's needed.
          </p>
        </div>

        <PacketStructureExplorer isDarkTheme={isDarkTheme} />
      </div>

      {/* Content Advertisement */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContentAd isDarkTheme={isDarkTheme} />
      </div>

      {/* Port Numbers Deep Dive */}
      <div className={`mt-12 rounded-xl shadow-lg p-6 ${
        isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${
          isDarkTheme ? 'text-white' : 'text-gray-800'
        }`}>
          <Hash className="w-8 h-8 text-purple-600" />
          Port Numbers: The Building Directory System
        </h2>
        
        <div className={`p-6 rounded-lg mb-8 ${
          isDarkTheme 
            ? 'bg-gradient-to-br from-purple-900 to-pink-900 border border-purple-700' 
            : 'bg-gradient-to-br from-purple-50 to-pink-50'
        }`}>
          <h3 className={`text-lg font-semibold mb-3 ${
            isDarkTheme ? 'text-purple-200' : 'text-purple-800'
          }`}>🏢 Think of Port Numbers Like Building Directory</h3>
          <p className={`leading-relaxed ${
            isDarkTheme ? 'text-purple-300' : 'text-purple-700'
          }`}>
            Port numbers work at the <strong>Transport Layer (Layer 4)</strong> and act like apartment numbers in a building. 
            While IP addresses tell us which building to go to, port numbers tell us exactly which apartment (application) 
            inside that building we want to reach. They work with TCP and UDP protocols to ensure each service gets its mail!
          </p>
        </div>

        <PortNumbersExplorer isDarkTheme={isDarkTheme} />
      </div>

      {/* Key Concepts */}
      <div className={`mt-12 rounded-xl shadow-lg p-6 ${
        isDarkTheme ? 'bg-gray-800 border border-gray-700' : 'bg-white'
      }`}>
        <h2 className={`text-2xl font-bold mb-6 ${
          isDarkTheme ? 'text-white' : 'text-gray-800'
        }`}>Key Concepts</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className={`p-4 rounded-lg ${
            isDarkTheme 
              ? 'bg-gradient-to-br from-blue-900 to-blue-800 border border-blue-700' 
              : 'bg-gradient-to-br from-blue-50 to-blue-100'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              isDarkTheme ? 'text-blue-200' : 'text-blue-800'
            }`}>Encapsulation</h3>
            <p className={`text-sm ${
              isDarkTheme ? 'text-blue-300' : 'text-blue-700'
            }`}>
              Each layer adds its own header (and sometimes trailer) to the data from the layer above.
            </p>
          </div>
          <div className={`p-4 rounded-lg ${
            isDarkTheme 
              ? 'bg-gradient-to-br from-green-900 to-green-800 border border-green-700' 
              : 'bg-gradient-to-br from-green-50 to-green-100'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              isDarkTheme ? 'text-green-200' : 'text-green-800'
            }`}>Abstraction</h3>
            <p className={`text-sm ${
              isDarkTheme ? 'text-green-300' : 'text-green-700'
            }`}>
              Each layer provides services to the layer above while hiding implementation details.
            </p>
          </div>
          <div className={`p-4 rounded-lg ${
            isDarkTheme 
              ? 'bg-gradient-to-br from-purple-900 to-purple-800 border border-purple-700' 
              : 'bg-gradient-to-br from-purple-50 to-purple-100'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              isDarkTheme ? 'text-purple-200' : 'text-purple-800'
            }`}>Protocol Stack</h3>
            <p className={`text-sm ${
              isDarkTheme ? 'text-purple-300' : 'text-purple-700'
            }`}>
              The complete set of protocols working together across all layers to enable communication.
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer Advertisement */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <FooterAd isDarkTheme={isDarkTheme} />
      </div>
    </div>
  );
};

export default NetworkLayersExplorer;